import { useLocation } from "react-router";
import { useFrontendTool } from "@copilotkit/react-core/v2";
import { z } from "zod";
import api from "core/api/api";
import propertyTemplateService from "core/services/metaStoreService/property-template-service";
import App from "app";
import { buildDryCompileTQL, dropDryCompileEntities } from "../utils/dry-compile-tql";
import { getTqlSkeletonForSourceOrTarget } from "./utils/get-tql-skeleton-for-source-or-target";
import { describePropertyTemplate } from "./utils/describe-property-template";
import componentService, { ComponentKeys } from "core/services/componentService/component-service";
import { componentID } from "core/services/componentService/component-service-base";
import commonAdapterNameService from "core/services/componentService/common-adapter-name-service";
import { dropApplication } from "./utils/drop-application";

export const useFlowDesignerBuilderTools = () => {
    const location = useLocation();
    const currentModule = location.pathname.split("/").filter(Boolean)[0] ?? null;

    useFrontendTool({
        name: "getMetaObjects",
        description:
            "Returns the IDs of all existing metaObjects of a given type in the MDR. " +
            "Call this BEFORE creating any new metaObject to avoid name collisions — " +
            "if the name you want is already present in the returned list, choose a different name.",
        parameters: z.object({
            type: z
                .string()
                .describe(
                    "The metaObject type to query. Valid values: 'NAMESPACE', 'DASHBOARD', 'PAGE', 'WACTIONSTORE', 'FLOW', 'APPLICATION', 'STREAM', 'WINDOW', 'CACHE', 'TARGET', 'TYPE', 'CQ', 'GROUP', 'PROPERTYTEMPLATE', 'DG', 'VISUALIZATION', 'SOURCE', 'EXTERNALSOURCE', 'SERVER', 'QUERYVISUALIZATION', 'PROPERTYSET', 'PROPERTYVARIABLE', 'OPENPROCESSOR', 'SYSALERTRULE', 'EXTERNALCACHE', 'ROUTER', 'SHIELDPROFILE', 'VAULT', 'USER', 'EMBEDDINGGENERATOR', 'CONNECTIONPROFILE', 'AIAGENT', 'SENTINEL'"
                )
        }),
        handler: async ({ type }: { type: string }) => {
            try {
                const data = await (api as any).getMetaObjectsByType([type]);
                const items: any[] = data?.[type] ?? [];
                return items.map((item: any) => item.id ?? item.uuid).filter(Boolean);
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
            }
        }
    });

    // const available = currentModule === "flow" ? "enabled" : "disabled";

    const available = true;

    useFrontendTool(
        {
            name: "getTQL",
            description: "Gets the TQL of an application",
            parameters: z.object({
                module: z.string().describe("The Module Name got from getCurrentPageContext tool"),
                appName: z
                    .string()
                    .describe("The namespace-qualified application name, e.g. 'admin.PosApp' — not just 'PosApp'.")
            }),
            handler: async ({ appName }: { appName: string }) => {
                try {
                    return await api.createTQL(appName, null);
                } catch (e) {
                    return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
                }
            }
        },
        [available]
    );

    useFrontendTool(
        {
            name: "validateTQL",
            description:
                "Validates a TQL statement. For an ALTER/CREATE APPLICATION, this compiles a renamed, throw-away " +
                "application (and its nested components, e.g. a SENTINEL or CQ being added to it) and always drops " +
                "it afterwards — no side effects. For a standalone entity with no APPLICATION wrapper " +
                "(CONNECTIONPROFILE, EMBEDDINGGENERATOR, ...), there is no throwaway copy — this compiles the TQL " +
                "for real. Returns 'OK' on success, or an error object on failure.",
            parameters: z.object({
                tql: z.string().describe("The TQL to validate")
            }),
            handler: async ({ tql }: { tql: string }) => {
                const { tql: dryCompileTql, entities } = buildDryCompileTQL(tql);
                await dropDryCompileEntities(api, entities);
                try {
                    await api.compileTQL(dryCompileTql);
                    return "OK";
                } catch (e) {
                    return { error: e.message ?? String(e) };
                } finally {
                    await dropDryCompileEntities(api, entities);
                }
            }
        },
        [available]
    );

    useFrontendTool(
        {
            name: "compileTQL",
            description:
                "Compiles AND Executes a TQL. Use this ONLY if a user explicitly asks to compile or create an application. ",
            parameters: z.object({
                tql: z.string().describe("The TQL to compile and detect errors")
            }),
            handler: async ({ tql }) => {
                try {
                    const result = await api.compileTQL(tql);

                    App.vent.trigger("compilation-needed");
                    return result;
                } catch (e) {
                    return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
                }
            }
        },
        [available]
    );

    useFrontendTool(
        {
            name: "dropApplication",
            description:
                "Drops (permanently deletes) an application. Call this AFTER the user has confirmed the destructive action " +
                "via confirmUser and it returned { confirmed: true }. Automatically stops and undeploys the application " +
                "first if it is currently running/deployed — the server refuses to drop a deployed application. Use this " +
                "ONLY for fully deleting an application, NOT for undeploying an application that should still exist (use " +
                "compileTQL with an ALTER APPLICATION ... UNDEPLOY statement for that instead). Returns the compile result " +
                "on success, or { error: '...' } on failure (e.g. another still-deployed app/flow depends on a shared " +
                "component of this one) — relay the error message to the user in that case, do not retry silently.",
            parameters: z.object({
                appName: z.string().describe("The namespace-qualified application name to drop, e.g. 'admin.PosApp'.")
            }),
            handler: async ({ appName }: { appName: string }) => {
                try {
                    return await dropApplication(appName, currentModule);
                } catch (e) {
                    console.error(e);
                    return { error: e.message ?? String(e) };
                }
            }
        },
        [available, currentModule]
    );

    useFrontendTool({
        name: "listPropertyTemplates",
        description:
            "List all Striim property templates (adapters) grouped by adapterType (e.g. source, target, parser, formatter). " +
            "Returns an object keyed by adapterType; each value is an array of { name, nsName, description }. " +
            "Use this to discover available adapters before calling getTqlSkeleton for full details.",
        parameters: z.object({}),
        handler: async () => {
            try {
                const grouped: Record<string, Array<{ name: string; nsName: string; description: string }>> = {};
                propertyTemplateService.propertyTemplates.forEach((model: any) => {
                    const t = model.toJSON();
                    const key = t.adapterType || "other";
                    (grouped[key] ||= []).push({
                        name: t.name,
                        nsName: t.nsName,
                        description: t.description || ""
                    });
                });
                return {
                    status: "success",
                    completed: true,
                    message:
                        "Successfully retrieved all available Striim property templates grouped by adapter type. This response only contains the list of available property templates. Use this information to select an adapter, and call getTqlSkeleton only if the user requests the details or skeleton for a specific property template.",
                    data: grouped
                };
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
            }
        }
    });

    useFrontendTool({
        name: "getTqlSkeleton",
        description:
            "Returns a ready-to-fill CREATE SOURCE / CREATE TARGET skeleton for a Striim adapter, built deterministically " +
            "from its property template. This is the single source of truth for an adapter's USING(...) fields. " +
            "Scaffold properties (required, conditionally required, defaulted, or enum) " +
            "are emitted as active lines: ready to compile when they have a concrete value, or as a '<PLACEHOLDER>' you fill in. " +
            "Each line carries an inline '-- ...' comment listing allowed values, defaults, and conditional-required rules. The " +
            "PARSE USING / FORMAT USING clause (when the adapter needs one) is included automatically AND scaffolded inline with " +
            "the parser's/formatter's own properties (column/row delimiters, quoting, encoding, etc.), as is the OUTPUT TO / " +
            "INPUT FROM clause. Every purely-optional property is also listed, as a commented '-- ... optional' line, so the skeleton " +
            "is the adapter's full field list — use it to confirm whether a field exists. After calling: rename the component, fill " +
            "the active lines with the user's values, uncomment any optional lines you need (keeping the '<PLACEHOLDER>' / " +
            "JDBC-template convention from the adapter_properties skill), and delete the rest. " +
            "CRITICAL — quoting: copy each returned property line's quoting EXACTLY as scaffolded. Every line is already " +
            "correctly quoted for its real type (booleans/numbers unquoted, e.g. header: TRUE; everything else — strings, " +
            "delimiters, enums like DSVFormatter's 'standard' — single-quoted, e.g. columndelimiter: ','). This applies " +
            "identically inside the PARSE USING / FORMAT USING block (DSVParser, DSVFormatter, etc.) — never re-derive, " +
            "reformat, or strip quotes from a scaffolded value when substituting the user's data; only replace the " +
            "'<PLACEHOLDER>' text between the existing quotes/bare-literal, never the quoting itself.",
        parameters: z.object({
            componentName: z
                .string()
                .describe(
                    "Adapter name exactly as it appears in TQL, e.g. 'DatabaseReader', 'OracleReader', 'SnowflakeWriter'."
                ),
            componentType: z
                .enum(["SOURCE", "TARGET"])
                .describe("Whether to scaffold a CREATE SOURCE or a CREATE TARGET."),
            inputStream: z
                .string()
                .optional()
                .describe("TARGET only — the stream the target reads from (INPUT FROM clause). Omit for sources."),
            parserOrFormatter: z
                .string()
                .optional()
                .describe(
                    "Parser (source) or formatter (target) name to use when the adapter requires one, e.g. 'DSVParser', 'JSONFormatter'. Defaults to DSVParser/DSVFormatter."
                )
        }),
        handler: async ({
            componentName,
            componentType,
            inputStream,
            parserOrFormatter
        }: {
            componentName: string;
            componentType: "SOURCE" | "TARGET";
            inputStream?: string;
            parserOrFormatter?: string;
        }) => {
            try {
                return getTqlSkeletonForSourceOrTarget(componentName, componentType, inputStream, parserOrFormatter);
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "AdapterResolutionError" };
            }
        }
    });

    useFrontendTool({
        name: "describePropertyTemplate",
        description:
            "Returns the full field list of a single Striim property template, built deterministically from its " +
            "propertyMap. Use this for adapter types that getTqlSkeleton does NOT cover — in particular " +
            "CONNECTIONPROFILE types (adapterType 'connectionprofile', e.g. 'Snowflake', 'S3', 'Databricks', " +
            "'GoogleKMS', 'OpenAI_CP', 'VertexAI_CP'), where there is no CREATE SOURCE/TARGET skeleton to scaffold. " +
            "Discover the template name first via listPropertyTemplates, then call this to learn its exact fields. " +
            "Returns { name, adapterType, requiresParser, requiresFormatter, properties[] }; each property carries " +
            "{ name, type ('string'|'number'|'boolean'|'password'|raw), required, requiredWhen?, default?, " +
            "allowedValues?, secret?, group? }. Emit every required (and met-conditional) field, using the concrete " +
            "default/enum value when present and a '<PLACEHOLDER>' otherwise; never invent a value for a 'secret' field.",
        parameters: z.object({
            name: z
                .string()
                .describe(
                    "Property template name exactly as it appears in listPropertyTemplates / TQL, e.g. 'Snowflake', 'GoogleKMS',..."
                )
        }),
        handler: async ({ name }: { name: string }) => {
            try {
                const template = describePropertyTemplate(name);
                if (template?.adapterType === "internal") {
                    return JSON.stringify({
                        status: "adapter-not-found",
                        message:
                            `The property template "${name}" is not available.` +
                            `Call adapterNotFound({ adapterName: "${name}" }) now.`
                    });
                }
                return template;
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "AdapterResolutionError" };
            }
        }
    });

    useFrontendTool({
        name: "getCommonAdapters",
        description:
            "Returns common adapters as an array of { commonName, IL, CDC } — e.g. { commonName: 'Oracle', IL, CDC }. " +
            "Use this to resolve a common database source (Oracle, MySQL, etc.) to its IL and CDC component names. " +
            "If the source is not listed here, use listAvailableComponents instead.",
        parameters: z.object({}),
        handler: () => {
            try {
                return commonAdapterNameService.getCommonNames().map((commonName: string) => ({
                    commonName,
                    IL: commonAdapterNameService.getILComponentName(commonName),
                    CDC: commonAdapterNameService.getCDCComponentName(commonName)
                }));
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
            }
        }
    });

    useFrontendTool({
        name: "listAvailableComponents",
        description:
            "Returns all SOURCE or TARGET components as an array of { componentName, humanReadableName, adapter } " +
            "e.g. { componentName: 'OracleReader', humanReadableName: 'Oracle', adapter: 'OracleReader' }. " +
            "Use this to find a component , match the user's input against componentName, humanReadableName, or adapter. ",
        parameters: z.object({
            type: z.enum(["SOURCE", "TARGET"]).describe("The component type to search: SOURCE or TARGET.")
        }),
        handler: ({ type }: { type: "SOURCE" | "TARGET" }) => {
            try {
                const components = [];
                for (const component of componentService.getAllComponents()) {
                    if (component[ComponentKeys.Type] !== type) continue;
                    components.push({
                        componentName: component[ComponentKeys.ComponentName],
                        humanReadableName: component[ComponentKeys.HumanReadableName],
                        adapter: component[ComponentKeys.Adapter]
                    });
                }
                return components;
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
            }
        }
    });

    useFrontendTool({
        name: "describeComponent",
        description: "Returns the full metadata for a single component, given its componentName.",
        parameters: z.object({
            componentName: z.string().describe("The componentName of the component to describe.")
        }),
        handler: ({ componentName }: { componentName: string }) => {
            try {
                const metadata = componentService.getMetadata(componentName as componentID);
                if (!metadata) {
                    return {
                        error: `No component found with componentName "${componentName}". Call listAvailableComponents to find a valid componentName.`
                    };
                }
                return metadata;
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "StriimAPIError" };
            }
        }
    });
};
